import { getInspectionDecisionLabel, getWorkflowStatusLabel } from "../constants";
import type { InspectionItemData } from "../types";
import { escapeHtml, renderPagePath } from "../view-helpers";

type ItemSectionSummary = {
  label: string;
  stateLabel: string;
  warning?: boolean;
  detail: string;
};

function buildSubitemSummary(item: InspectionItemData): {
  completed: number;
  notInvolved: number;
  pending: number;
} {
  const completed = item.subitems.filter((subitem) => subitem.workflowStatus === "completed").length;
  const notInvolved = item.subitems.filter((subitem) => subitem.workflowStatus === "not_involved").length;
  const pending = item.subitems.length - completed - notInvolved;

  return { completed, notInvolved, pending };
}

function buildItemSummaries(item: InspectionItemData): ItemSectionSummary[] {
  const summary = buildSubitemSummary(item);
  const finished = summary.pending === 0;
  const itemNotInvolved = item.decision === "not_involved";

  return [
    {
      label: "子项完成",
      stateLabel: finished ? "已齐" : `${summary.pending} 个待补充`,
      warning: !finished,
      detail: finished
        ? `当前 ${item.subitems.length} 个子项都已完成或不涉及。`
        : `当前已完成 ${summary.completed} 个，另有 ${summary.notInvolved} 个不涉及。`,
    },
    {
      label: "整项评定",
      stateLabel: getInspectionDecisionLabel(item.decision),
      warning: item.decision === "pending",
      detail:
        item.decision === "pending"
          ? "整项评定还没有形成稳定结果，先继续补齐子项。"
          : "整项评定会跟随子项结果自动汇总，不需要另外手动计算。",
    },
    {
      label: "整项状态",
      stateLabel: itemNotInvolved ? "不涉及" : getWorkflowStatusLabel(item.workflowStatus),
      detail: itemNotInvolved
        ? "当前整项已设为不涉及，下面全部子项都会统一按不涉及处理。"
        : "整项完成状态会跟随子项自动变化，不需要手动确认。",
    },
    {
      label: "下一步衔接",
      stateLabel: finished ? "可回列表" : "继续补子项",
      warning: !finished,
      detail: finished
        ? "当前单项已经够完整，可以回到单项列表继续检查其它单项。"
        : "建议优先点进待补充的子项，把文本、图片和评定补齐。",
    },
  ];
}

function findNextSubitem(item: InspectionItemData) {
  return (
    item.subitems.find((subitem) => subitem.workflowStatus !== "completed" && subitem.workflowStatus !== "not_involved")
    || item.subitems[0]
    || null
  );
}

function renderSubitemRow(item: InspectionItemData): string {
  return item.subitems
    .map(
      (subitem, index) => {
        const statusClass =
          subitem.workflowStatus === "completed" || subitem.workflowStatus === "not_involved"
            ? "is-finished"
            : "is-pending";

        return `
        <button
          class="panel subitem-row ${statusClass}"
          data-subitem-id="${subitem.subitemId}"
        >
          <div class="subitem-head">
            <strong>${index + 1}. ${subitem.subitemName}</strong>
            <span class="status-pill">${getWorkflowStatusLabel(subitem.workflowStatus)}</span>
          </div>
          <p class="muted">${escapeHtml(subitem.findings || "还没填写抽查情况。")}</p>
          <div class="subitem-meta">
            <span>抽查数量：${subitem.sampleCount || "未填写"}</span>
            <span>图片：${subitem.photos.length} 张</span>
            <span>评定：${getInspectionDecisionLabel(subitem.decision)}</span>
          </div>
        </button>
      `;
      },
    )
    .join("");
}

export function renderInspectionItemDetailPage(
  projectId: string,
  projectName: string,
  item: InspectionItemData,
): string {
  const summary = buildSubitemSummary(item);

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-items">返回单项列表</button>
        <h1>单项详情</h1>
      </header>
      ${renderPagePath([
        { label: projectName, href: `#/projects/home?id=${projectId}` },
        { label: "现场评定", href: `#/projects/inspection-items?id=${projectId}` },
        { label: item.itemName },
      ])}
      <div class="panel stack">
        <p class="eyebrow">当前单项</p>
        <h2>${escapeHtml(item.itemName)}</h2>
        <p class="muted">共 ${item.subitems.length} 个子项，逐项进入填写抽查数量、抽查部位、抽查情况和评定结果。单项完成状态会根据子项完成情况自动变化。</p>
        <div class="inline-pills">
          <span class="state-pill">完成状态：${getWorkflowStatusLabel(item.workflowStatus)}</span>
          <span class="state-pill">评定汇总：${getInspectionDecisionLabel(item.decision)}</span>
          <span class="state-pill">已完成 ${summary.completed} 个</span>
          <span class="state-pill">${summary.pending} 个待补充</span>
          <span class="state-pill">${summary.notInvolved} 个不涉及</span>
        </div>
        <button class="ghost-button ${item.decision === "not_involved" ? "is-selected" : ""}" type="button" data-action="toggle-item-not-involved">整项设为不涉及</button>
        ${
          item.decision === "not_involved"
            ? `<p class="muted">该单项已设为不涉及。系统已把本单项下全部子项统一置为“不涉及”，无需再填写任何内容。</p>`
            : `<p class="muted">当本单项下全部子项都达到“已完成/不涉及”时，单项会自动变为完成。</p>`
        }
      </div>
      <div class="stack">
        ${renderSubitemRow(item)}
      </div>
    </section>
  `;
}
